/**
 * Loads serialized craft.js nodes into the editor WITHOUT polluting the
 * undo/redo history (QA-025).
 *
 * craft.js records `deserialize` as an undoable action (it is not in its
 * `ignoreHistoryForActions` list), so loading a segment — or switching
 * between segments — used to land on the undo stack like a normal edit.
 * Pressing Undo after a segment switch could then restore the PREVIOUSLY
 * open segment's content into the current segment's canvas, and the builder
 * would persist those nodes onto the wrong segment.
 *
 * This helper:
 *  1. deserializes through `actions.history.ignore()` so the load itself is
 *     never undoable, and
 *  2. clears the history stacks so undo/redo always operate strictly within
 *     the currently loaded segment.
 *
 * @param {object} actions - craft.js `actions` from `useEditor()`.
 * @param {string|object} nodes - serialized nodes (JSON string or node map).
 * @returns {boolean} true if the nodes were loaded.
 */
export const loadSegmentNodes = (actions, nodes) => {
  if (!actions?.history) return false;
  if (!nodes) return false;
  try {
    actions.history.ignore().deserialize(nodes);
    actions.history.clear();
    return true;
  } catch (error) {
    console.error("Error deserializing nodes:", error);
    return false;
  }
};

/**
 * True when `nodes` is an EMPTY or ROOT-less serialized craft map (ISP-9999 V2).
 *
 * Staging segments persist an empty segment as the string `"{}"`. Feeding that to
 * craft's `deserialize` resolves to `replaceNodes({})`, leaving the editor store
 * with ZERO nodes and NO ROOT — so the `<Frame>` has no valid drop parent and
 * every block drop throws `ERROR_INVALID_NODEID` (hover also throws the observed
 * `Invariant failed`). A map without a `ROOT` key is equally unusable as a canvas.
 *
 * Callers use this to SKIP deserialize for such maps so the fresh empty
 * `EmptyContainerWidget` ROOT that the `<Frame>` builds for the segment survives
 * as a valid (empty) drop target, instead of being wiped.
 *
 * @param {string|object|null|undefined} nodes - serialized nodes (JSON string or map).
 * @returns {boolean} true if the map is empty / has no ROOT / is unparseable.
 */
export const isEmptyNodeMap = (nodes) => {
  if (nodes === null || nodes === undefined || nodes === "") return true;
  try {
    const map = typeof nodes === "string" ? JSON.parse(nodes) : nodes;
    if (!map || typeof map !== "object") return true;
    if (Object.keys(map).length === 0) return true; // "{}"
    return !("ROOT" in map); // rootless map — no valid canvas root
  } catch (error) {
    // Unparseable -> treat as empty; let the <Frame> build its own ROOT rather
    // than crash the editor with a bad deserialize.
    return true;
  }
};

/**
 * True when `nodes` carries NO real block content (ISP-9999 round 2, #builder-fresh-unsaved).
 *
 * Broader than `isEmptyNodeMap` above on purpose — that predicate answers "is this safe to
 * deserialize", so a `ROOT` with an empty `nodes: []` child list (the canonical
 * `EmptyContainerWidget` template `newSegment()`/`initialSegmentNode` both use, and what a
 * freshly-built `<Frame>` serializes for a never-touched segment) correctly counts as NOT
 * empty there — deserializing it is perfectly safe. But for deciding whether a segment's
 * content genuinely CHANGED, that same "ROOT with zero children" map carries exactly as much
 * content as `null`/`"{}"`/a rootless map: none. Opening an empty segment lazily materializes
 * this canonical ROOT purely as a side effect of mounting the editor — comparing it against
 * a differently-empty saved value (`null` on prod, `"{}"` on some staging records) must not
 * read as a content change, or every empty-segment OPEN falsely dirties the wheel.
 *
 * @param {string|object|null|undefined} nodes - serialized nodes (JSON string or map).
 * @returns {boolean} true if the map has no ROOT children and no linked nodes.
 */
export const hasNoCraftContent = (nodes) => {
  if (isEmptyNodeMap(nodes)) return true;
  try {
    const map = typeof nodes === "string" ? JSON.parse(nodes) : nodes;
    const root = map.ROOT;
    const hasChildren = Array.isArray(root?.nodes) && root.nodes.length > 0;
    const hasLinkedNodes =
      root?.linkedNodes && Object.keys(root.linkedNodes).length > 0;
    return !hasChildren && !hasLinkedNodes;
  } catch (error) {
    return true;
  }
};
